import { useState, useEffect, useCallback } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface CarouselProps {
  images: { src: string; alt: string }[];
  slidesToShow?: number;
  autoplayInterval?: number;
}

export default function FacilityCarousel({ images, slidesToShow = 4, autoplayInterval = 5000 }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(slidesToShow);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const ref = useScrollAnimation<HTMLDivElement>(0.1);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(window.innerWidth < 768 ? 1 : slidesToShow);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [slidesToShow]);

  const maxIndex = Math.max(0, images.length - visibleCount);

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    const timer = setInterval(next, autoplayInterval);
    return () => clearInterval(timer);
  }, [next, autoplayInterval]);

  return (
    <>
      <div ref={ref} className="scroll-animate py-12 md:py-20 overflow-hidden">
        <div className="relative px-4 md:px-6">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
          >
            {images.map((img, i) => (
              <div
                key={i}
                className="flex-shrink-0 px-2 md:px-3"
                style={{ width: `${100 / visibleCount}%` }}
              >
                <div
                  className="relative aspect-[3/2] overflow-hidden rounded-lg bg-gray-100 cursor-pointer"
                  onClick={() => setModalIndex(i)}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <p className="mt-3 text-center text-sm text-gray-700 font-medium">{img.alt}</p>
              </div>
            ))}
          </div>
          <button
            onClick={prev}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/90 text-gray-800 hover:bg-white transition z-10 cursor-pointer"
            aria-label="이전"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-xl" />
            </div>
          </button>
          <button
            onClick={next}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/90 text-gray-800 hover:bg-white transition z-10 cursor-pointer"
            aria-label="다음"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl" />
            </div>
          </button>
        </div>
      </div>

      {/* Modal */}
      {modalIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 transition-opacity duration-300"
          onClick={() => setModalIndex(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[modalIndex].src}
              alt={images[modalIndex].alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setModalIndex(null)}
              className="absolute -top-10 right-0 w-8 h-8 flex items-center justify-center text-white hover:text-gray-300 transition cursor-pointer"
              aria-label="닫기"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}